import type {
  DomainWorkblockDefinition,
  ExecutionFlowDefinition,
  FrameworkSnapshot,
  RoleDefinition,
  WorkMomentDefinition,
  PublishedBusinessModuleDefinition,
  ProjectedBusinessModuleDefinition
} from "@apf/framework-config";
import type {
  IntegrationModuleBinding,
  IntegrationRecipeDefinition,
  PublishedIntegrationToolRecipe,
  PublishedIntegrationRegistry
} from "@apf/framework-config";

/** The versioned GroundWorks hierarchy used by every framework surface. */
export type DomainWorkblock = DomainWorkblockDefinition;
export type BusinessModule = ProjectedBusinessModuleDefinition | PublishedBusinessModuleDefinition;
export type InternalBusinessModule = ProjectedBusinessModuleDefinition;
export type PublishedBusinessModule = PublishedBusinessModuleDefinition;
export type ExecutionFlow = ExecutionFlowDefinition | PublishedBusinessModuleDefinition["executionFlows"][number];
export type RolePlacement = DomainWorkblockDefinition["roleCards"][number];

export interface RoleCatalogueEntry {
  definition: RoleDefinition;
  placements: Array<{ domainWorkblock: DomainWorkblock; role: RolePlacement }>;
  relatedBusinessModuleIds: string[];
}

export interface JobCatalogueEntry {
  businessModule: BusinessModule;
  domainWorkblock: DomainWorkblock;
  roles: RoleCatalogueEntry[];
}

export type WorkblockDimensionFacetParam = "segment" | "buying" | "treatment";

export interface WorkblockDimensionFacetDefinition {
  param: WorkblockDimensionFacetParam;
  dimensionId: string;
  memberOrder: readonly string[];
}

export const workblockDimensionFacetDefinitions: readonly WorkblockDimensionFacetDefinition[] = [
  { param: "segment", dimensionId: "spend-segment", memberOrder: ["direct", "indirect"] },
  { param: "buying", dimensionId: "buying-mode", memberOrder: ["goods", "services", "mixed"] },
  { param: "treatment", dimensionId: "accounting-treatment", memberOrder: ["capital", "operating"] }
] as const;

/**
 * Dimension facets are an all-or-nothing publication contract. The resolver
 * suppresses incomplete applicability; this browser-side boundary also keeps
 * a partial or stale snapshot from activating a facet.
 */
export function activeWorkblockDimensionFacetDefinitions(
  snapshot: FrameworkSnapshot
): WorkblockDimensionFacetDefinition[] {
  const modules = snapshot.businessModuleRegistry.businessModules;
  if (!modules.length) return [];

  return workblockDimensionFacetDefinitions.filter((facet) => {
    const dimension = snapshot.groundworks.dimensions?.find(({ id }) => id === facet.dimensionId);
    if (
      !dimension ||
      dimension.status !== "governed" ||
      dimension.siteFacet?.parameter !== facet.param
    ) return false;

    const governedMemberIds = new Set(
      dimension.members
        .filter(({ status }) => status === "governed")
        .map(({ id }) => id)
    );
    if (!governedMemberIds.size) return false;

    return modules.every((module) => {
      const entries = dimensionApplicabilityForModule(module)
        .filter(({ dimensionId }) => dimensionId === facet.dimensionId);
      if (entries.length !== 1) return false;
      const memberIds = entries[0]!.memberIds;
      return memberIds.length > 0 &&
        new Set(memberIds).size === memberIds.length &&
        memberIds.every((memberId) => governedMemberIds.has(memberId));
    });
  });
}

export interface WorkMomentCatalogueFilters {
  query?: string;
  roleIds?: readonly string[];
  jobIds?: readonly string[];
  domainIds?: readonly string[];
  office?: readonly string[];
  proof?: readonly string[];
}

export interface WorkMomentResolved {
  definition: WorkMomentDefinition;
  businessModule: BusinessModule;
  domainWorkblock: DomainWorkblock;
  perspective: WorkMomentDefinition["perspectives"][number];
  jobStory: BusinessModule["jobStories"][number];
  agent: NonNullable<FrameworkSnapshot["content"]["workMomentAgents"]>[number];
  fixture: NonNullable<FrameworkSnapshot["content"]["workMomentFixtures"]>[number];
}

export interface WorkblockFacetFilters {
  query?: string;
  roleIds?: readonly string[];
  jobIds?: readonly string[];
  dimensionMemberIds?: Partial<Record<WorkblockDimensionFacetParam, readonly string[]>>;
}

export interface WorkblockFacetMatch {
  domainWorkblock: DomainWorkblock;
  matchedRoleIds: string[];
  matchedJobIds: string[];
  matchedDimensionModules: Array<{
    businessModuleId: string;
    matchedDimensionMemberIds: Partial<Record<WorkblockDimensionFacetParam, string[]>>;
  }>;
  textMatch?: {
    kind: "workblock" | "role" | "job";
    label: string;
    roleId?: string;
    jobId?: string;
  };
}

/**
 * Deduplicate facet state and place projected IDs in catalogue order. IDs that
 * are not in the current projection stay in their original order so the UI can
 * report them as unavailable until the reader explicitly clears them.
 */
export function canonicalizeFacetIds(
  selectedIds: readonly string[],
  projectedCatalogueIds: readonly string[]
): string[] {
  const remaining = new Set(selectedIds);
  const projected = projectedCatalogueIds.flatMap((id) => remaining.delete(id) ? [id] : []);
  return [...projected, ...remaining];
}

export function isInternalBusinessModule(module: BusinessModule): module is InternalBusinessModule {
  /* Agents now travel in the published projection too, so the internal lens
   * is discriminated by the fields that stay internal-only. */
  return "roadmap" in module && "learningLoop" in module;
}

export function isInternalExecutionFlow(flow: ExecutionFlow): flow is ExecutionFlowDefinition {
  return "tasks" in flow && "inputs" in flow && "outputs" in flow && "stateSurfaces" in flow;
}

export function businessModulesFor(
  snapshot: FrameworkSnapshot,
  domainWorkblockId: string
): BusinessModule[] {
  const byId = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));
  const domainWorkblock = snapshot.content.domainWorkblocks.find(({ id }) => id === domainWorkblockId);
  if (!domainWorkblock) return [];
  return domainWorkblock.businessModuleIds.flatMap((id) => {
    const module = byId.get(id);
    return module ? [module] : [];
  });
}

export function domainWorkblockForModule(
  snapshot: FrameworkSnapshot,
  businessModule: Pick<BusinessModule, "domainWorkblockId">
): DomainWorkblock | undefined {
  return snapshot.content.domainWorkblocks.find(({ id }) => id === businessModule.domainWorkblockId);
}

export function domainWorkblockPath(domainWorkblockId: string): string {
  return `/workblocks/${domainWorkblockId}`;
}

export function businessModulePath(domainWorkblockId: string, businessModuleId: string): string {
  return `${domainWorkblockPath(domainWorkblockId)}/business-modules/${businessModuleId}`;
}

export function rolePath(roleId: string): string {
  return `/reference/roles/${encodeURIComponent(roleId)}`;
}

export function workMomentPath(momentId: string, roleId?: string): string {
  const path = `/reference/work-moments/${encodeURIComponent(momentId)}`;
  return roleId ? `${path}?role=${encodeURIComponent(roleId)}` : path;
}

export function promptPath(domainWorkblockId: string, businessModuleId?: string): string {
  const base = `${domainWorkblockPath(domainWorkblockId)}/build-prompt`;
  return businessModuleId ? `${base}?module=${encodeURIComponent(businessModuleId)}` : base;
}

export function modulePathFromId(snapshot: FrameworkSnapshot, businessModuleId: string): string | undefined {
  const module = snapshot.businessModuleRegistry.businessModules.find(({ id }) => id === businessModuleId);
  return module ? businessModulePath(module.domainWorkblockId, module.id) : undefined;
}

/** Canonical recipe identity: a stable route, not a query-plus-fragment. */
export function integrationRecipePath(recipeId: string): string {
  return `/connected-work/recipes/${encodeURIComponent(recipeId)}`;
}

/** Canonical example identity: a stable route, not a fragment. */
export function integrationExamplePath(exampleId: string): string {
  return `/connected-work/examples/${encodeURIComponent(exampleId)}`;
}

/** Canonical outcome-tool recipe identity. */
export function integrationToolPath(toolId: string): string {
  return `/connected-work/tools/${encodeURIComponent(toolId)}`;
}

/** Canonical system-profile identity. */
export function integrationSystemPath(systemId: string): string {
  return `/connected-work/systems/${encodeURIComponent(systemId)}`;
}

/** Canonical mapping-profile identity. */
export function integrationMappingPath(mappingId: string): string {
  return `/connected-work/mappings/${encodeURIComponent(mappingId)}`;
}

/** Canonical standard/format identity. */
export function integrationStandardPath(formatId: string): string {
  return `/connected-work/standards/${encodeURIComponent(formatId)}`;
}

/** Canonical downloadable-asset identity on the governed downloads page. */
export function downloadAssetPath(assetId: string): string {
  return `/downloads/${encodeURIComponent(assetId)}`;
}

/** Canonical shared-entity identity in the data-model reference. */
export function groundworksEntityPath(entityId: string): string {
  return `/groundworks/${encodeURIComponent(entityId)}`;
}

/** Canonical Office capability identity. */
export function officeCapabilityPath(productId: string): string {
  return `/capabilities/${encodeURIComponent(productId)}`;
}

/** Canonical public repository identity. Every GitHub link derives from it. */
export const groundworksRepositoryUrl = "https://github.com/bwllaming/GroundWorks";

/** Canonical Beyond Work Partner License location in the public repository. */
export const partnerLicenseUrl = `${groundworksRepositoryUrl}/blob/main/LICENSE`;

export function workblockSourceUrl(workblockPath: string): string {
  return `${groundworksRepositoryUrl}/tree/main/${workblockPath}`;
}

export function integrationBindingForModule(
  integrations: PublishedIntegrationRegistry,
  businessModuleId: string
): IntegrationModuleBinding | undefined {
  return integrations.moduleBindings.find((binding) => binding.businessModuleId === businessModuleId);
}

export function integrationRecipesForModule(
  integrations: PublishedIntegrationRegistry,
  businessModuleId: string
): IntegrationRecipeDefinition[] {
  const binding = integrationBindingForModule(integrations, businessModuleId);
  if (!binding) return [];
  const ids = new Set([...binding.inboundRecipeIds, ...binding.outboundRecipeIds]);
  return integrations.recipes.filter(({ id }) => ids.has(id));
}

export function integrationToolForModule(
  integrations: PublishedIntegrationRegistry,
  businessModuleId: string
): PublishedIntegrationToolRecipe | undefined {
  return integrations.toolRecipes.find((tool) => tool.businessModuleId === businessModuleId);
}

interface DimensionApplicabilityDefinition {
  dimensionId: string;
  memberIds: string[];
}

function dimensionApplicabilityForModule(
  businessModule: BusinessModule
): DimensionApplicabilityDefinition[] {
  const candidate = (businessModule as BusinessModule & {
    dimensionApplicability?: unknown;
  }).dimensionApplicability;
  if (!Array.isArray(candidate)) return [];
  return candidate.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const dimensionId = "dimensionId" in entry && typeof entry.dimensionId === "string"
      ? entry.dimensionId
      : undefined;
    const rawMemberIds = "memberIds" in entry && Array.isArray(entry.memberIds)
      ? entry.memberIds
      : undefined;
    if (!dimensionId || !rawMemberIds?.length) return [];
    const memberIds = [...new Set(rawMemberIds.filter((value): value is string => typeof value === "string" && value.length > 0))];
    return memberIds.length ? [{ dimensionId, memberIds }] : [];
  });
}

export function workblockDimensionMemberIds(
  businessModule: BusinessModule,
  dimensionId: string
): string[] {
  return dimensionApplicabilityForModule(businessModule).find((entry) => entry.dimensionId === dimensionId)?.memberIds ?? [];
}

function copyValue(snapshot: FrameworkSnapshot, key: string): string {
  return snapshot.copy[key] ?? key;
}

function projectedRoles(snapshot: FrameworkSnapshot): RoleDefinition[] {
  return snapshot.content.roles ?? [];
}

function projectedWorkMoments(snapshot: FrameworkSnapshot): WorkMomentDefinition[] {
  return snapshot.content.workMoments ?? [];
}

/** One canonical role row, joined to every projected contextual placement. */
export function roleDefinitions(snapshot: FrameworkSnapshot): RoleCatalogueEntry[] {
  const placementsById = new Map<string, RoleCatalogueEntry["placements"]>();
  for (const domainWorkblock of snapshot.content.domainWorkblocks) {
    for (const role of domainWorkblock.roleCards) {
      placementsById.set(role.id, [
        ...(placementsById.get(role.id) ?? []),
        { domainWorkblock, role }
      ]);
    }
  }
  return projectedRoles(snapshot).flatMap((definition) => {
    const placements = placementsById.get(definition.id) ?? [];
    if (!placements.length) return [];
    const relatedBusinessModuleIds = [...new Set(
      placements.flatMap(({ role }) => [
        ...role.performsBusinessModuleIds,
        ...role.consultedBusinessModuleIds
      ])
    )];
    return [{ definition, placements, relatedBusinessModuleIds }];
  });
}

export function rolePlacements(snapshot: FrameworkSnapshot, roleId: string): RoleCatalogueEntry["placements"] {
  return roleDefinitions(snapshot).find(({ definition }) => definition.id === roleId)?.placements ?? [];
}

export function rolesForDomain(snapshot: FrameworkSnapshot, domainWorkblockId: string): RoleCatalogueEntry[] {
  return roleDefinitions(snapshot).filter(({ placements }) =>
    placements.some(({ domainWorkblock }) => domainWorkblock.id === domainWorkblockId)
  );
}

export function jobsForRole(snapshot: FrameworkSnapshot, roleId: string, domainWorkblockId?: string): BusinessModule[] {
  const role = roleDefinitions(snapshot).find(({ definition }) => definition.id === roleId);
  if (!role) return [];
  const ids = new Set(role.placements.flatMap(({ domainWorkblock, role: placement }) =>
    domainWorkblockId && domainWorkblock.id !== domainWorkblockId
      ? []
      : [...placement.performsBusinessModuleIds, ...placement.consultedBusinessModuleIds]
  ));
  return snapshot.businessModuleRegistry.businessModules.filter((module) => ids.has(module.id));
}

export function rolesForJob(snapshot: FrameworkSnapshot, businessModuleId: string): RoleCatalogueEntry[] {
  return roleDefinitions(snapshot).filter(({ placements }) => placements.some(({ role }) =>
    role.performsBusinessModuleIds.includes(businessModuleId) ||
    role.consultedBusinessModuleIds.includes(businessModuleId)
  ));
}

export function jobCatalogue(snapshot: FrameworkSnapshot): JobCatalogueEntry[] {
  const domains = new Map(snapshot.content.domainWorkblocks.map((domain) => [domain.id, domain]));
  return snapshot.businessModuleRegistry.businessModules.flatMap((businessModule) => {
    const domainWorkblock = domains.get(businessModule.domainWorkblockId);
    return domainWorkblock ? [{ businessModule, domainWorkblock, roles: rolesForJob(snapshot, businessModule.id) }] : [];
  });
}

export function workMomentsForRole(snapshot: FrameworkSnapshot, roleId: string): WorkMomentDefinition[] {
  return projectedWorkMoments(snapshot).filter((moment) => moment.perspectives.some((perspective) => perspective.roleId === roleId));
}

export function workMomentsForJob(snapshot: FrameworkSnapshot, businessModuleId: string): WorkMomentDefinition[] {
  return projectedWorkMoments(snapshot).filter((moment) => moment.businessModuleId === businessModuleId);
}

export function workMomentsForDomain(snapshot: FrameworkSnapshot, domainWorkblockId: string): WorkMomentDefinition[] {
  const moduleIds = new Set(businessModulesFor(snapshot, domainWorkblockId).map(({ id }) => id));
  return projectedWorkMoments(snapshot).filter((moment) => moduleIds.has(moment.businessModuleId));
}

export function resolveWorkMoment(
  snapshot: FrameworkSnapshot,
  momentId: string,
  roleId?: string
): WorkMomentResolved | undefined {
  const definition = projectedWorkMoments(snapshot).find((moment) => moment.id === momentId);
  if (!definition) return undefined;
  const businessModule = snapshot.businessModuleRegistry.businessModules.find(({ id }) => id === definition.businessModuleId);
  if (!businessModule) return undefined;
  const domainWorkblock = snapshot.content.domainWorkblocks.find(({ id }) => id === businessModule.domainWorkblockId);
  if (!domainWorkblock) return undefined;
  const perspective = definition.perspectives.find((candidate) => candidate.roleId === roleId) ??
    definition.perspectives.find((candidate) => candidate.roleId === definition.defaultPerspectiveRoleId) ??
    definition.perspectives[0];
  const jobStory = perspective
    ? businessModule.jobStories.find(({ id }) => id === perspective.jobId)
    : undefined;
  const agent = snapshot.content.workMomentAgents?.find(({ id }) => id === definition.agentId);
  const fixture = snapshot.content.workMomentFixtures?.find(({ id }) => id === definition.fixtureId);
  return perspective && jobStory && agent && fixture
    ? { definition, businessModule, domainWorkblock, perspective, jobStory, agent, fixture }
    : undefined;
}

export function workMomentCatalogue(
  snapshot: FrameworkSnapshot,
  filters: WorkMomentCatalogueFilters = {}
): WorkMomentResolved[] {
  const query = filters.query?.trim().toLocaleLowerCase() ?? "";
  const roleIds = new Set(filters.roleIds ?? []);
  const jobIds = new Set(filters.jobIds ?? []);
  const domainIds = new Set(filters.domainIds ?? []);
  const office = new Set(filters.office ?? []);
  const proof = new Set(filters.proof ?? []);
  return projectedWorkMoments(snapshot).flatMap((definition) => {
    const preferredRole = definition.perspectives.find((perspective) => roleIds.has(perspective.roleId))?.roleId;
    const resolved = resolveWorkMoment(snapshot, definition.id, preferredRole);
    if (!resolved) return [];
    const claim = snapshot.differentiators.claims.find(({ id }) => id === definition.primaryDifferentiatorClaimId);
    if (roleIds.size && !definition.perspectives.some(({ roleId }) => roleIds.has(roleId))) return [];
    if (jobIds.size && !jobIds.has(definition.businessModuleId)) return [];
    if (domainIds.size && !domainIds.has(resolved.domainWorkblock.id)) return [];
    if (office.size && !definition.officeSequence?.some((capability) => office.has(capability))) return [];
    if (proof.size && !proof.has(claim?.maturity ?? resolved.businessModule.implementationStatus)) return [];
    const text = [
      copyValue(snapshot, definition.titleKey),
      copyValue(snapshot, definition.summaryKey),
      copyValue(snapshot, resolved.perspective.responsibilityKey),
      copyValue(snapshot, resolved.perspective.beforeKey),
      copyValue(snapshot, resolved.perspective.nowKey),
      copyValue(snapshot, resolved.jobStory.storyKey),
      copyValue(snapshot, resolved.jobStory.completionKey),
      resolved.agent.label,
      copyValue(snapshot, resolved.businessModule.labelKey),
      copyValue(snapshot, resolved.domainWorkblock.nameKey)
    ].join(" ").toLocaleLowerCase();
    return !query || text.includes(query) ? [resolved] : [];
  });
}

/** Relationship-aware Workblock matching. OR within facets, AND across them. */
export function workblockMatchesFacets(
  snapshot: FrameworkSnapshot,
  filters: WorkblockFacetFilters = {}
): WorkblockFacetMatch[] {
  const query = filters.query?.trim().toLocaleLowerCase() ?? "";
  const selectedRoles = new Set(filters.roleIds ?? []);
  const selectedJobs = new Set(filters.jobIds ?? []);
  const selectedDimensionMemberIds = new Map<WorkblockDimensionFacetParam, Set<string>>(
    workblockDimensionFacetDefinitions.map(({ param }) => [
      param,
      new Set(filters.dimensionMemberIds?.[param] ?? [])
    ])
  );
  const activeDimensionFacetDefinitions = activeWorkblockDimensionFacetDefinitions(snapshot).filter(
    ({ param }) => (selectedDimensionMemberIds.get(param)?.size ?? 0) > 0
  );
  const canonicalRoleById = new Map(
    roleDefinitions(snapshot).map(({ definition }) => [definition.id, definition])
  );
  return snapshot.content.domainWorkblocks.flatMap((domainWorkblock) => {
    const modules = businessModulesFor(snapshot, domainWorkblock.id);
    const dimensionMatchedModules = activeDimensionFacetDefinitions.length
      ? modules.filter((module) => activeDimensionFacetDefinitions.every(({ param, dimensionId }) => {
          const applicableMemberIds = new Set(workblockDimensionMemberIds(module, dimensionId));
          const requestedMemberIds = selectedDimensionMemberIds.get(param);
          return requestedMemberIds
            ? [...requestedMemberIds].some((memberId) => applicableMemberIds.has(memberId))
            : true;
        }))
      : modules;
    const scopedJobs = selectedJobs.size
      ? dimensionMatchedModules.filter((module) => selectedJobs.has(module.id))
      : dimensionMatchedModules;
    const matchedRoles = domainWorkblock.roleCards.filter((role) =>
      selectedRoles.has(role.id) && (
        !selectedJobs.size && !activeDimensionFacetDefinitions.length
          ? true
          : scopedJobs.some((module) =>
              role.performsBusinessModuleIds.includes(module.id) || role.consultedBusinessModuleIds.includes(module.id)
            )
      )
    );
    const matchedJobs = dimensionMatchedModules.filter((module) => selectedJobs.has(module.id));
    if (selectedRoles.size && !matchedRoles.length) return [];
    if (selectedJobs.size && !matchedJobs.length) return [];
    if (activeDimensionFacetDefinitions.length && !dimensionMatchedModules.length) return [];
    const workblockLabel = copyValue(snapshot, domainWorkblock.nameKey);
    const textCandidates = query ? [
      ...[
        domainWorkblock.id,
        workblockLabel,
        copyValue(snapshot, domainWorkblock.labelKey),
        copyValue(snapshot, domainWorkblock.descriptorKey),
        copyValue(snapshot, domainWorkblock.descriptionKey),
        copyValue(snapshot, domainWorkblock.outcomeKey),
        ...(domainWorkblock.signatureKey ? [copyValue(snapshot, domainWorkblock.signatureKey)] : [])
      ].map((value) => ({
        value,
        match: { kind: "workblock" as const, label: workblockLabel }
      })),
      ...domainWorkblock.roleCards.flatMap((role) => {
        const canonical = canonicalRoleById.get(role.id);
        const label = canonical
          ? copyValue(snapshot, canonical.labelKey)
          : copyValue(snapshot, role.roleKey);
        return [
          copyValue(snapshot, role.roleKey),
          copyValue(snapshot, role.mandateKey),
          copyValue(snapshot, role.questionKey),
          ...(canonical ? [
            label,
            copyValue(snapshot, canonical.descriptionKey),
            ...(canonical.searchTermKeys ?? []).map((key) => copyValue(snapshot, key))
          ] : [])
        ].map((value) => ({
          value,
          match: { kind: "role" as const, label, roleId: role.id }
        }));
      }),
      ...modules.flatMap((module) => {
        const label = copyValue(snapshot, module.labelKey);
        const jobMatch = { kind: "job" as const, label, jobId: module.id };
        return [
          ...[
            label,
            copyValue(snapshot, module.descriptorKey),
            copyValue(snapshot, module.descriptionKey),
            copyValue(snapshot, module.jobStatementKey)
          ].map((value) => ({ value, match: jobMatch })),
          ...module.jobStories.flatMap((story) => [
            { value: copyValue(snapshot, story.storyKey), match: jobMatch },
            { value: copyValue(snapshot, story.completionKey), match: jobMatch }
          ])
        ];
      })
    ] : [];
    const textMatch = query
      ? textCandidates.find(({ value }) => value.toLocaleLowerCase().includes(query))?.match
      : undefined;
    if (query && !textMatch) return [];
    return [{
      domainWorkblock,
      matchedRoleIds: matchedRoles.map(({ id }) => id),
      matchedJobIds: matchedJobs.map(({ id }) => id),
      matchedDimensionModules: dimensionMatchedModules.map((module) => ({
        businessModuleId: module.id,
        matchedDimensionMemberIds: Object.fromEntries(
          activeDimensionFacetDefinitions.map(({ param, dimensionId }) => [
            param,
            [...(selectedDimensionMemberIds.get(param) ?? [])].filter((memberId) =>
              workblockDimensionMemberIds(module, dimensionId).includes(memberId)
            )
          ])
        )
      })),
      textMatch
    }];
  });
}
